/**
 * The job card's collapsed History timeline (production-records R2) — every recorded production
 * event on this order, newest first, with the actor the state rows never kept. Collapsed by default
 * and fetched only on first expand, so the working job card pays nothing for its history.
 *
 * Honesty rule: the log began recording at the R1 deploy ({@link EVENT_LOG_EPOCH_LABEL}) — the
 * empty state says so, so an empty timeline is never read as "this order was never produced"
 * (exactly the misread that started the production-records work). R1.1 closed the batch holes:
 * whole-batch close/undo/delete now write per-garment + per-order events too.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, History } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { formatStamp, GARMENT_TONE_CLASS, type JobCardGarment } from './order-detail-lib';
import {
  EVENT_LOG_EPOCH_LABEL,
  eventLabel,
  eventTone,
  type ProductionEvent,
} from './production-event-lib';

export function OrderHistorySection({
  storeId,
  orderId,
  garments,
}: {
  storeId: string;
  orderId: string;
  garments: JobCardGarment[];
}) {
  const client = useApiClient();
  const [open, setOpen] = useState(false);
  const [lineFilter, setLineFilter] = useState('all');

  const eventsQuery = useQuery({
    queryKey: ['order-events', storeId, orderId],
    // Fetch on first expand only — <details> stays a zero-cost affordance until used. The query
    // stays live afterwards (change-feed invalidation), so re-expanding is instant.
    enabled: open,
    queryFn: async () => {
      const { data, error } = await client.GET('/orders/{storeId}/{orderId}/events', {
        params: { path: { storeId, orderId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the history.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const byLine = new Map(garments.map((g) => [g.lineId, g]));
  const events = eventsQuery.data?.events ?? [];
  const filtered = lineFilter === 'all' ? events : events.filter((e) => e.lineId === lineFilter);
  // Offer the garment filter only when it can actually narrow something.
  const filterable = garments.length > 1 && events.some((e) => e.lineId);

  return (
    <details
      className="group rounded-md border"
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="order-history"
    >
      {/* 44px under a thumb (measured 356x36 at 390px before this), 36px on a desktop. Same floor the
          board's sheet chips document. */}
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm md:min-h-9">
        <History className="size-4 text-muted-foreground" />
        <span className="font-medium">History</span>
        <span className="text-xs text-muted-foreground">who did what, when — undos included</span>
        <ChevronDown className="ml-auto size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="space-y-2 border-t px-3 py-2">
        {filterable && (
          <Select value={lineFilter} onValueChange={setLineFilter}>
            <SelectTrigger className="h-8 w-64 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All garments</SelectItem>
              {garments.map((g) => (
                <SelectItem key={g.lineId} value={g.lineId}>
                  {g.title}
                  {g.variantTitle ? ` — ${g.variantTitle}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {eventsQuery.isPending && open && <Skeleton className="h-16 w-full" />}
        {eventsQuery.isError && (
          <p className="py-1 text-sm text-destructive">
            Couldn&apos;t load the history — refresh the page to retry.
          </p>
        )}
        {eventsQuery.isSuccess && filtered.length === 0 && (
          <p className="py-1 text-sm text-muted-foreground">
            {lineFilter === 'all'
              ? `No recorded events. The event log began ${EVENT_LOG_EPOCH_LABEL} — earlier production left no per-action history.`
              : 'No recorded events for this garment — its work may predate the event log.'}
          </p>
        )}
        {filtered.length > 0 && (
          <ol className="divide-y">
            {filtered.map((e) => (
              <HistoryRow
                key={e.id}
                event={e}
                garment={e.lineId ? byLine.get(e.lineId) : undefined}
              />
            ))}
          </ol>
        )}
      </div>
    </details>
  );
}

function HistoryRow({ event, garment }: { event: ProductionEvent; garment?: JobCardGarment }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1.5 text-sm">
      <span
        className="whitespace-nowrap text-xs tabular-nums text-muted-foreground"
        title={new Date(event.at).toLocaleString()}
      >
        {formatStamp(event.at)}
      </span>
      <Badge variant="outline" className={GARMENT_TONE_CLASS[eventTone(event.type)]}>
        {eventLabel(event)}
      </Badge>
      {garment && (
        <span className="min-w-0 truncate">
          {garment.title}
          {garment.variantTitle && (
            <span className="text-muted-foreground"> — {garment.variantTitle}</span>
          )}
        </span>
      )}
      {event.batchId && (
        <Link
          to="/batches"
          search={{ batch: event.batchId }}
          className="text-xs underline underline-offset-2 hover:no-underline"
        >
          batch ↗
        </Link>
      )}
      <span className="ml-auto text-xs text-muted-foreground">{event.actor}</span>
    </li>
  );
}
